import { useMemo } from "react";
import {
  applyProductDiscount,
  formatProductUsdPrice,
  getProductUnitLabel,
  getTieredPriceRangeLabel,
  getTieredPricingDisplayRows,
  normalizeTieredPricing,
} from "../../utils/productTradeSpecs";

export interface ProductTieredPricingBlockProps {
  product: any;
  /** Product-level discount percentage applied to each tier price. */
  discountPct?: number;
  /** Visual theme aligned with Bizcover India product detail. */
  variant?: "bizcover" | "default";
  /** Hide top range summary (label/headline/unit) and keep table only. */
  hideSummary?: boolean;
}

/**
 * Alibaba-style tiered pricing: headline range + volume/price table at top of product detail.
 */
export function ProductTieredPricingBlock({
  product,
  discountPct = 0,
  variant = "default",
  hideSummary = false,
}: ProductTieredPricingBlockProps) {
  const unitLabel = useMemo(() => getProductUnitLabel(product), [product]);
  const tiers = useMemo(
    () => normalizeTieredPricing(product?.tieredPricing),
    [product?.tieredPricing],
  );
  const displayRows = useMemo(
    () => getTieredPricingDisplayRows(tiers, unitLabel || "pieces"),
    [tiers, unitLabel],
  );
  const headline = useMemo(
    () => getTieredPriceRangeLabel(tiers, discountPct),
    [tiers, discountPct],
  );

  if (!tiers.length || (!headline && !hideSummary)) return null;

  const isBizcover = variant === "bizcover";
  const accentText = isBizcover ? "text-[#FF4400]" : "text-orange-600";
  const headlineClass = isBizcover
    ? "text-[32px] font-extrabold tracking-tight tabular-nums"
    : "text-2xl font-bold tabular-nums text-gray-900";
  const labelClass = isBizcover
    ? "text-[13px] font-medium text-[#637381]"
    : "text-sm font-medium text-gray-500";
  const tableBorder = isBizcover ? "border-[#e9eef4]" : "border-gray-200";
  const rowText = isBizcover ? "text-[13px] text-[#374151]" : "text-sm text-gray-700";
  const rowPrice = isBizcover
    ? "text-[14px] font-bold tabular-nums text-[#FF4400]"
    : "text-sm font-bold tabular-nums text-orange-600";
  const headerBg = isBizcover ? "bg-[#f8fafc]" : "bg-gray-50";
  const headerText = isBizcover
    ? "text-[12px] font-semibold text-[#374151]"
    : "text-xs font-semibold text-gray-700";

  const perUnitClass = isBizcover
    ? "mt-1 text-[14px] text-[#4b5563]"
    : "mt-1 text-sm text-gray-600";

  return (
    <div className="mt-4">
      {!hideSummary && (
        <>
          <p className={labelClass}>Price in USD</p>
          <div className="mt-1">
            <div className="flex flex-wrap items-end gap-2">
              <span className={`${headlineClass} ${accentText}`}>{headline}</span>
            </div>
            {unitLabel ? (
              <p className={perUnitClass}>
                <span className={isBizcover ? "text-[#637381]" : "text-gray-500"}>Per </span>
                {unitLabel}
              </p>
            ) : null}
          </div>
        </>
      )}

      <div
        className={`${hideSummary ? "mt-0" : "mt-4"} overflow-hidden rounded-lg border ${tableBorder} bg-white`}
      >
        <div
          className={`grid grid-cols-[1fr_auto] items-center gap-4 border-b px-4 py-3 ${tableBorder} ${headerBg}`}
        >
          <span className={headerText}>Min. order</span>
          <span className={`${headerText} min-w-[88px] text-right`}>Unit price</span>
        </div>
        {displayRows.map((row, index) => {
          const price = applyProductDiscount(row.price, discountPct);
          return (
            <div
              key={`${row.volumeLabel}-${index}`}
              className={`grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3 ${
                index < displayRows.length - 1 ? `border-b ${tableBorder}` : ""
              }`}
            >
              <span className={`${rowText} min-w-0 leading-snug`}>{row.volumeLabel}</span>
              <span className={`${rowPrice} min-w-[88px] text-right`}>
                {formatProductUsdPrice(price)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
